import { useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Grid, Stack, Typography, Skeleton, Alert } from "@mui/material"
import { fetchIdCardSubscriptions, IdCardSubscriptionsResponse, LoginQuery } from "../../loginApi"
import { StandardButton } from "../../../../shared/components/buttons/StandardButton"
import { IdCardSignupFormContentCompany } from "./IdCardSignupFormContentCompany"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { IdCardSignupFormValues, IdCardSubscriptionUser, TransformedCompany } from "../../loginTypes"
import { IdCardSignupStepHeader } from "./IdCardSignupStepHeader"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { FormikSubmitButton } from "../../../../shared/components/forms/Formik"
import { AppError } from "../../../../core/api/errorTypes"

const useStyles = makeStyles()((theme) => ({
    bodyText: {
        color: theme.palette.text.secondary,
        fontWeight: "normal",
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    box: {
        gap: "0.5",
        padding: "1rem",
        border: "1px solid rgba(0, 0, 0, 0.56)",
        borderRadius: ".75rem",
        color: theme.palette.text.primary,
        cursor: "pointer",
        minHeight: "8rem",
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.1)",
        },
    },
    selectedBox: {
        border: "2px solid #2196F3",
        backgroundColor: "#2196F3",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#37A0F4",
        },
    },
    selectedBodyText: {
        color: "#fff",
    },
    skeletonBox: {
        gap: "0.5",
        padding: "1rem",
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: ".75rem",
        minHeight: "8rem",
    },
    nextButton: {
        width: "100%",
        [theme.breakpoints.up("sm")]: {
            width: "50%",
        },
    },
    errorWrapper: {
        margin: "1rem 0",
    },
}))

const transformCardSubscriptionsData = (data: IdCardSubscriptionsResponse) => data.cardSubscriptions || []

type IdCardSignupFormContentProps = Readonly<{
    activationToken: string
    companiesList: TransformedCompany[]
    isUserExists: boolean
    existingUser?: IdCardSubscriptionUser
    errorMessage?: string | null
    setErrorMessage: (message: string | null) => void
}>

export const IdCardSignupFormContent = ({
    activationToken,
    companiesList,
    isUserExists,
    existingUser,
    errorMessage,
    setErrorMessage,
}: IdCardSignupFormContentProps) => {
    const { classes, cx } = useStyles()
    const { values, setFieldValue } = useFormikContext<IdCardSignupFormValues>()
    const [activeStep, setActiveStep] = useState(0)

    const selectedCompany = companiesList.find((company) => company.id === values.companyId)

    const {
        data: subscriptionOptions = [],
        isLoading: isLoadingSubscriptions,
        error,
    } = useApiQuery(fetchIdCardSubscriptions, {
        queryName: LoginQuery.ID_CARDS_SUBSCRIPTIONS,
        dependencies: {
            activationToken,
            companyId: selectedCompany ? selectedCompany.companyId : 0,
            userGroupId: values.userGroupId ? +values.userGroupId : 0,
        },
        enabled: activeStep === 1 && !!values.companyId && !!values.userGroupId,
        select: transformCardSubscriptionsData,
    })

    const renderStepContent = (step: number, setFieldValue: any, values: IdCardSignupFormValues) => {
        switch (step) {
            case 0:
                return (
                    <Stack gap="1rem" maxWidth={{ sm: "100%", md: "65%" }}>
                        <IdCardSignupFormContentCompany
                            existingUser={existingUser}
                            isUserExists={isUserExists}
                            companiesList={companiesList}
                        />

                        <LocalizedStrict id="id-card-signup-next-button">
                            <StandardButton
                                fullWidth
                                variant="contained"
                                onClick={handleNext}
                                type="button"
                                disabled={
                                    (!isUserExists && values.displayName.trim().length < 3) ||
                                    !values.companyId ||
                                    !values.userGroupId
                                }
                                // disabled={!isUserExists && values.displayName.trim().length < 3}
                            >
                                Next
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                )
            case 1:
                return (
                    <Stack gap="4rem">
                        {errorMessage ? (
                            <Alert severity="error" className={classes.errorWrapper}>
                                {errorMessage}
                            </Alert>
                        ) : (
                            <>
                                <Grid container gap="1rem">
                                    {isLoadingSubscriptions ? (
                                        <>
                                            {[1, 2].map((item) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={5.75}
                                                    key={`skeleton-${item}`}
                                                    className={classes.skeletonBox}
                                                >
                                                    <Skeleton variant="text" width="60%" height={28} />
                                                    <Skeleton variant="text" width="90%" height={20} />
                                                    <Skeleton variant="text" width="75%" height={20} />
                                                </Grid>
                                            ))}
                                        </>
                                    ) : subscriptionOptions.length > 0 ? (
                                        subscriptionOptions.map((option) => (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={5.8}
                                                key={option.id}
                                                onClick={() => setFieldValue("subscriptionId", option.id)}
                                                className={cx(classes.box, {
                                                    [classes.selectedBox]: values?.subscriptionId === option.id,
                                                })}
                                            >
                                                <Typography variant="subtitle1">{option.name}</Typography>
                                                <Typography
                                                    variant="body2"
                                                    title={option.description}
                                                    className={cx(classes.bodyText, {
                                                        [classes.selectedBodyText]:
                                                            values?.subscriptionId === option.id,
                                                    })}
                                                >
                                                    {option.description}
                                                </Typography>
                                            </Grid>
                                        ))
                                    ) : (
                                        <Grid item xs={12}>
                                            {error instanceof AppError ? (
                                                <Typography>{error.displayMessage}</Typography>
                                            ) : (
                                                <LocalizedStrict id="id-card-signup-no-subscriptions">
                                                    <Typography>No subscription options available.</Typography>
                                                </LocalizedStrict>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                                <Stack alignItems="start" gap="1rem">
                                    <LocalizedStrict id="id-card-signup-next-button">
                                        <FormikSubmitButton
                                            fullWidth={false}
                                            className={classes.nextButton}
                                            variant="contained"
                                            parentDisabled={!values.subscriptionId}
                                        >
                                            Next
                                        </FormikSubmitButton>
                                    </LocalizedStrict>
                                </Stack>
                            </>
                        )}
                    </Stack>
                )
            default:
                return <></>
        }
    }

    const handleNext = () => setActiveStep((prevStep) => prevStep + 1)
    const handleBack = () => {
        setErrorMessage(null)
        setActiveStep((prevStep) => prevStep - 1)
    }

    return (
        <>
            <Stack gap="2rem">
                <IdCardSignupStepHeader handleBack={handleBack} step={activeStep} />
                {renderStepContent(activeStep, setFieldValue, values)}
            </Stack>
        </>
    )
}
